/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Hub } from 'aws-amplify/utils';
import { getAuthUser, getIdToken, signOut as authSignOut, AuthUser } from '@/lib/auth';

interface AuthContextType {
  user: AuthUser | null;
  idToken: string | null;
  loading: boolean;
  signOut: () => void;
  refreshUser: () => Promise<AuthUser | null>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async (): Promise<AuthUser | null> => {
    try {
      const [authUser, token] = await Promise.all([getAuthUser(), getIdToken()]);
      setUser(authUser);
      setIdToken(token);
      return authUser;
    } catch {
      setUser(null);
      setIdToken(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshUser();

    // Listen for Amplify auth events
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
        case 'signedIn':
          refreshUser();
          break;
        case 'signedOut':
          setUser(null);
          setIdToken(null);
          break;
      }
    });

    return () => unsubscribe();
  }, []);

  const signOut = () => {
    authSignOut();
    setUser(null);
    setIdToken(null);
  };

  return (
    <AuthContext.Provider value={{ user, idToken, loading, signOut, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
